import { RuleSection } from '../types';

export const major01to05Sections: RuleSection[] = [
  {
    "id": "m01-overview",
    "title": "Karczmy i Katedry: co zmienia ten dodatek",
    "requiredModuleIds": [
      "major-01-inns-cathedrals"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-01",
      "corpus-base"
    ],
    "keywords": [
      "karczma",
      "katedra"
    ],
    "level": "intermediate",
    "hubGroup": "Karczmy i Katedry",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stare Karczmy i Katedry (0 za niedokończenie).",
        "oldEditionName": "stare Karczmy i Katedry (0 za niedokończenie)"
      },
      {
        "type": "paragraph",
        "text": "Karczmy i Katedry wzmacniają punktowanie dróg i miast oraz dodają dużą figurę do walki o większość. W tym rozdziale zobaczysz, które elementy należy włączyć do partii, dlaczego dodatek podnosi stawkę przy budowie dużych obszarów i gdzie szukać pełnych procedur dla każdej nowej zasady. Podstawowy przebieg tury, legalność kafelków i zasada większości pozostają bez zmian."
      },
      {
        "type": "paragraph",
        "text": "Wtasuj kafelki dodatku do wspólnej puli krajobrazu. Dodaj duże meeple graczy oraz elementy szóstego gracza, jeśli bierze udział sześć osób. Karczma zmienia wartość ukończonej drogi, katedra wartość ukończonego miasta, a duży meeple ma siłę 2 przy ustalaniu większości. Nie oznacza to, że kafelki lub herby liczą się podwójnie."
      },
      {
        "type": "image",
        "imageId": "large-meeple",
        "alt": "Duży meeple — siła 2.",
        "caption": "Duży meeple"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Karczma", "Gdy punktuje droga z karczmą.", "Ukończona droga jest warta 2 punkty za kafelek."],
          ["Katedra", "Gdy punktuje miasto z katedrą.", "Ukończone miasto daje 3 za kafelek i 3 za herb."],
          ["Duży meeple", "Podczas większości na zajętym obszarze.", "Ma siłę 2, lecz nie mnoży punktów."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Stosowanie starej zasady 0 punktów za nieukończoną drogę z karczmą lub miasto z katedrą. Korekta: w C3.1 takie obszary punktują na końcu według normalnych wartości końcowych."
      },
      {
        "type": "crossReference",
        "sectionId": "m01-inns",
        "label": "Szczegółowo: karczmy"
      },
      {
        "type": "crossReference",
        "sectionId": "m01-cathedrals",
        "label": "Szczegółowo: katedry"
      },
      {
        "type": "crossReference",
        "sectionId": "m01-large-meeple",
        "label": "Szczegółowo: duży meeple"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Wtasuj kafelki dodatku i przygotuj duże meeple.",
          "Karczmy wzmacniają ukończone drogi, a katedry ukończone miasta.",
          "Duży meeple ma siłę 2 tylko przy większości.",
          "Nie używaj dawnych kar C1/C2 za nieukończone obszary."
        ]
      },
      {
        "type": "qa",
        "question": "Czy Karczmy i Katedry dodają osobną fazę tury?",
        "answer": "Nie. Nowe elementy działają w zwykłych etapach dokładania kafelka, wystawienia figury i punktowania."
      }
    ],
    "imageIds": [
      "large-meeple"
    ]
  },
  {
    "id": "m01-inns",
    "title": "Karczmy: wzmocnione punktowanie dróg",
    "requiredModuleIds": [
      "major-01-inns-cathedrals"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-01",
      "corpus-base"
    ],
    "keywords": [
      "karczma"
    ],
    "level": "intermediate",
    "hubGroup": "Karczmy i Katedry",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stare Karczmy i Katedry (0 za niedokończenie).",
        "oldEditionName": "stare Karczmy i Katedry (0 za niedokończenie)"
      },
      {
        "type": "paragraph",
        "text": "Karczma zwiększa nagrodę za ukończenie drogi, która ją zawiera. Wystarczy jedna karczma na całej połączonej drodze: po ukończeniu każdy kafelek tej drogi jest wart 2 punkty zamiast 1. Kolejne karczmy na tej samej drodze nie mnożą tej wartości."
      },
      {
        "type": "paragraph",
        "text": "Karczma działa wyłącznie przy punktowaniu ukończonej drogi w trakcie gry. Jeżeli droga pozostanie nieukończona na końcu partii, w C3.1 nadal daje 1 punkt za każdy kafelek. To najważniejsza różnica względem starszych wydań."
      },
      {
        "type": "scoringTable",
        "headers": [
          "Droga z karczmą",
          "Punkty"
        ],
        "rows": [
          [
            "Ukończona",
            "2/kafelek"
          ],
          [
            "Nieukończona C3.1",
            "1/kafelek"
          ]
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma podróżnika na drodze z czterech kafelków, na jednym z nich jest karczma.",
            "Bartek dokłada kafelek, który zamyka drogę.",
            "1. Droga jest ukończona. 2. Liczy się pięć kafelków. 3. Karczma występuje na tej drodze. 4. Anna otrzymuje 2 × 5 punktów i odzyskuje podróżnika.",
            "Anna otrzymuje 10 punktów.",
            "Jedna karczma podwaja wartość każdego kafelka ukończonej drogi; nie zależy to od autora ostatniego kafelka."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Podwajanie wartości drogi ponownie za każdą kolejną karczmę. Korekta: obecność co najmniej jednej karczmy ustawia wartość na 2 za kafelek."
      },
      {
        "type": "crossReference",
        "sectionId": "roads",
        "label": "Podstawowe ukończenie i punktowanie dróg"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ukończona droga z karczmą: 2 za każdy kafelek.",
          "Kilka karczm nie zwiększa wartości ponad 2 za kafelek.",
          "Nieukończona droga z karczmą w C3.1: 1 za kafelek na końcu gry."
        ]
      },
      {
        "type": "qa",
        "question": "Czy nieukończona droga z karczmą daje 0 punktów?",
        "answer": "Nie. W wersji C3.1 daje na końcu gry po 1 punkcie za każdy kafelek."
      }
    ]
  },
  {
    "id": "m01-cathedrals",
    "title": "Katedry: wzmocnione punktowanie miast",
    "requiredModuleIds": [
      "major-01-inns-cathedrals"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-01",
      "corpus-base"
    ],
    "keywords": [
      "katedra"
    ],
    "level": "intermediate",
    "hubGroup": "Karczmy i Katedry",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stare Karczmy i Katedry (0 za niedokończenie).",
        "oldEditionName": "stare Karczmy i Katedry (0 za niedokończenie)"
      },
      {
        "type": "paragraph",
        "text": "Katedra zwiększa wartość każdego kafelka i herbu w ukończonym mieście, które ją zawiera. Przy ukończeniu takie miasto daje 3 punkty za kafelek i 3 punkty za herb. Liczba katedr w tym samym mieście nie zwiększa wartości ponownie."
      },
      {
        "type": "paragraph",
        "text": "Katedra nie zmienia wymogu ukończenia: miasto musi nie mieć otwartych krawędzi miasta. Jeżeli pozostanie nieukończone po ostatniej turze, w C3.1 punktuje normalnie po 1 za kafelek i herb, a nie za 0."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina kontroluje ukończone miasto z trzema kafelkami, dwoma herbami i katedrą.",
            "Rozpatruje miasto po dołożeniu zamykającego kafelka.",
            "1. Miasto jest ukończone. 2. Liczy 3 kafelki i 2 herby. 3. Katedra zmienia wartość na 3 za każdy z tych elementów. 4. Celina odzyskuje rycerza.",
            "Celina otrzymuje 15 punktów.",
            "Katedra dotyczy zarówno kafelków, jak i herbów w całym połączonym mieście."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Stosowanie wartości 3 za kafelek do miasta, które nie zostało ukończone. Korekta: na końcu gry nieukończone miasto z katedrą używa zwykłej wartości 1 za kafelek i herb."
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Podstawowe ukończenie i punktowanie miast"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ukończone miasto z katedrą: 3 za kafelek i 3 za herb.",
          "Wystarczy jedna katedra; kolejne nie mnożą wartości.",
          "Nieukończone miasto C3.1: 1 za kafelek i herb na końcu."
        ]
      },
      {
        "type": "qa",
        "question": "Czy katedra zwiększa punkty za herb?",
        "answer": "Tak. W ukończonym mieście z katedrą każdy herb jest wart 3 punkty."
      }
    ]
  },
  {
    "id": "m01-large-meeple",
    "title": "Duży meeple: siła 2 w większości",
    "requiredModuleIds": [
      "major-01-inns-cathedrals"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-01",
      "corpus-base"
    ],
    "keywords": [
      "duzy meeple"
    ],
    "level": "intermediate",
    "hubGroup": "Karczmy i Katedry",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stare Karczmy i Katedry (0 za niedokończenie).",
        "oldEditionName": "stare Karczmy i Katedry (0 za niedokończenie)"
      },
      {
        "type": "paragraph",
        "text": "Duży meeple jest dodatkową figurą gracza, którą wystawia się według zwykłych zasad na legalnym, niezajętym obszarze nowego kafelka. Jego jedyna szczególna właściwość dotyczy większości: ma siłę 2 zamiast 1. Może więc samodzielnie przeważyć nad jedną zwykłą figurą albo wyrównać siłę dwóch zwykłych figurek."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie dużego meepla jako dwóch punktów na kafelku. Korekta: jego siła 2 działa tylko przy porównaniu większości; wartość drogi, miasta, klasztoru lub pola liczy się normalnie."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma duży meeple w mieście, a Bartek ma w tym samym mieście zwykłego rycerza.",
            "Miasto zostaje ukończone.",
            "1. Anna ma siłę 2. 2. Bartek ma siłę 1. 3. Anna ma większość. 4. Tylko Anna otrzymuje pełną wartość miasta.",
            "Anna punktuje miasto i obie figury wracają do właścicieli.",
            "Duży meeple nie daje dodatkowych punktów, lecz rozstrzyga większość siłą 2."
          ]
        ]
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Podstawowe zasady większości i remisów"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Duży meeple jest wystawiany jak zwykła figura.",
          "Ma siłę 2 przy większości.",
          "Nie mnoży punktów obszaru.",
          "Wraca po zapunktowaniu ukończonego obszaru."
        ]
      },
      {
        "type": "qa",
        "question": "Czy duży meeple daje dwa razy więcej punktów?",
        "answer": "Nie. Jego wartość 2 służy wyłącznie do ustalania większości."
      }
    ]
  },
  {
    "id": "m02-overview",
    "title": "Kupcy i Budowniczowie: towary, tempo i pola",
    "requiredModuleIds": [
      "major-02-traders-builders"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-02",
      "corpus-base"
    ],
    "keywords": [
      "towary",
      "budowniczy",
      "swinia"
    ],
    "level": "intermediate",
    "hubGroup": "Kupcy i Budowniczowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stara świnia końcowa.",
        "oldEditionName": "stara świnia końcowa"
      },
      {
        "type": "paragraph",
        "text": "Kupcy i Budowniczowie dodają trzy powiązane decyzje: towary nagradzają gracza, który zamyka miasto, budowniczy pozwala rozwinąć własny obszar dodatkową turą, a świnia zamienia odpowiedni moment punktowania pola w natychmiastową premię. Dodatek nie zmienia legalności kafelków ani podstawowych zasad większości."
      },
      {
        "type": "image",
        "imageId": "goods-builder-pig",
        "alt": "Towary, budowniczy, świnia.",
        "caption": "Kupcy C3.1"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Towary", "Przy ukończeniu miasta z symbolami.", "Żetony bierze gracz kończący miasto; końcowe większości dają po 10."],
          ["Budowniczy", "Gdy przedłużasz własny obszar z budowniczym.", "Po pełnym rozpatrzeniu tury wykonujesz drugą pełną turę."],
          ["Świnia", "Po ukończeniu miasta z co najmniej jednym herbem.", "Punktuje 3 razy liczbę ukończonych miast przy jej polu i wraca."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Przyznawanie towarów graczowi z większością w mieście. Korekta: towary bierze gracz, który ukończył miasto, nawet jeśli nie ma w nim rycerza."
      },
      {
        "type": "crossReference",
        "sectionId": "m02-goods",
        "label": "Szczegółowo: towary"
      },
      {
        "type": "crossReference",
        "sectionId": "m02-builder",
        "label": "Szczegółowo: budowniczy i dodatkowa tura"
      },
      {
        "type": "crossReference",
        "sectionId": "m02-pig",
        "label": "Szczegółowo: świnia C3.1"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Towary należą do gracza kończącego miasto.",
          "Budowniczy daje dodatkową pełną turę po przedłużeniu własnego obszaru.",
          "Świnia punktuje po mieście z herbem, nie na końcu gry.",
          "Podstawowe reguły miasta, pola i większości nadal obowiązują."
        ]
      },
      {
        "type": "qa",
        "question": "Czy towary są punktowane od razu po zdobyciu?",
        "answer": "Nie. Żetony zbiera się w trakcie gry, a ich większości punktują na końcu gry."
      }
    ],
    "imageIds": [
      "goods-builder-pig"
    ]
  },
  {
    "id": "m02-goods",
    "title": "Towary: kto bierze żetony i jak liczyć końcową większość",
    "requiredModuleIds": [
      "major-02-traders-builders"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-02",
      "corpus-base"
    ],
    "keywords": [
      "towary"
    ],
    "level": "intermediate",
    "hubGroup": "Kupcy i Budowniczowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stara świnia końcowa.",
        "oldEditionName": "stara świnia końcowa"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Towary nagradzają gracza, który domyka miasto, a niekoniecznie tego, kto je kontroluje. Dzięki temu zamykanie miasta przeciwnika nadal może być korzystne. Punkty za towary pojawiają się dopiero na końcu gry, gdy porównujecie większości każdego rodzaju."
      },
      {
        "type": "numberedList",
        "items": [
          "Gdy miasto zostaje ukończone, gracz, który dołożył zamykający kafelek, bierze żetony towarów za wszystkie symbole widoczne w tym mieście.",
          "Następnie miasto punktuje według większości rycerzy i zwykłych zasad miasta.",
          "Żetony towarów nie dają punktów natychmiast; zbieraj je według rodzaju.",
          "Na końcu gry porównaj większości każdego rodzaju: gracz z największą liczbą danego rodzaju dostaje 10 punktów; przy remisie każdy remisujący dostaje pełne 10."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek kończy miasto Anny. W mieście są dwa symbole towarów. Anna ma w nim rycerza, Bartek nie.",
            "Bartek zabiera żetony, a potem punktuje się miasto.",
            "1. Bartek bierze dwa żetony. 2. Anna ma większość w mieście. 3. Anna dostaje punkty za miasto i odzyskuje rycerza. 4. Żetony Bartka czekają na koniec gry.",
            "Bartek ma dwa towary, Anna punkty za miasto.",
            "Towary należą do gracza kończącego miasto, niezależnie od większości rycerzy."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Przyznanie towarów graczowi z większością w mieście. Korekta: żetony bierze osoba, która ukończyła miasto."
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie towarów od razu po zdobyciu. Korekta: większość każdego rodzaju liczy się dopiero na końcu gry."
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Podstawowe punktowanie miasta"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Kolejność punktowania końcowego"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Żetony bierze gracz kończący miasto.",
          "Miasto punktuje osobno według większości.",
          "Na końcu: 10 punktów za większość każdego rodzaju.",
          "Remis: każdy remisujący dostaje pełne 10."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę wziąć towary z miasta, w którym nie mam rycerza?",
        "answer": "Tak. Towary przyznaje się za ukończenie miasta, a nie za większość w nim."
      }
    ]
  },
  {
    "id": "m02-builder",
    "title": "Budowniczy: przedłużenie obszaru i dodatkowa tura",
    "requiredModuleIds": [
      "major-02-traders-builders"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-02",
      "corpus-base"
    ],
    "keywords": [
      "budowniczy"
    ],
    "level": "intermediate",
    "hubGroup": "Kupcy i Budowniczowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stara świnia końcowa.",
        "oldEditionName": "stara świnia końcowa"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Budowniczy przyspiesza rozbudowę własnej drogi lub miasta. Ma siłę 0, więc nie pomaga w większości i nie może stać sam. Przypisujesz go do własnego podróżnika albo rycerza, a gdy przedłużysz ten obszar, zyskujesz dodatkową pełną turę."
      },
      {
        "type": "resolutionOrder",
        "title": "Jak rozstrzygnąć dodatkową turę",
        "steps": [
          "Dołóż kafelek, który przedłuża obszar z twoim budowniczym.",
          "Dokończ pozostałe części bieżącej tury, w tym punktowanie ukończeń.",
          "Zdejmij budowniczego z obszaru i odłóż go obok stosów.",
          "Odzyskaj budowniczego do swojego zasobu przed dodatkową turą.",
          "Rozegraj drugą pełną turę: dobierz i dołóż kafelek, opcjonalnie wystaw figurę, rozpatrz ukończenia."
        ]
      },
      {
        "type": "warning",
        "text": "Budowniczy wraca do zasobu PRZED dodatkową turą. Nie wraca automatycznie na planszę."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma rycerza i budowniczego w nieukończonym mieście.",
            "Dokłada kafelek, który przedłuża to miasto, lecz go nie zamyka.",
            "1. Przedłuża obszar. 2. Kończy etap figury i punktowanie. 3. Zdejmuje budowniczego. 4. Odzyskuje go. 5. Rozgrywa drugą pełną turę.",
            "Anna wykonała dwie pełne tury z rzędu.",
            "Dodatkowa tura powstaje po przedłużeniu obszaru z budowniczym i po pełnym rozpatrzeniu pierwszej tury."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pozostawienie budowniczego na planszy podczas dodatkowej tury. Korekta: najpierw wraca do zasobu, dopiero potem zaczyna się druga tura."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie budowniczego do większości. Korekta: jego siła wynosi 0."
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Podstawowy przebieg tury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Budowniczy ma siłę 0 i nie stoi sam.",
          "Przypisujesz go do własnego podróżnika lub rycerza.",
          "Przedłużenie obszaru daje dodatkową pełną turę.",
          "Budowniczy wraca do zasobu przed tą dodatkową turą."
        ]
      },
      {
        "type": "qa",
        "question": "Czy budowniczy wraca na planszę po dodatkowej turze?",
        "answer": "Nie automatycznie. Wraca do zasobu przed dodatkową turą; możesz wystawić go ponownie dopiero wtedy, gdy w którejś turze legalnie go przypiszesz."
      }
    ]
  },
  {
    "id": "m02-pig",
    "title": "Świnia C3.1: premia za pola przy miastach z herbem",
    "requiredModuleIds": [
      "major-02-traders-builders"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-02",
      "corpus-base"
    ],
    "keywords": [
      "swinia"
    ],
    "level": "intermediate",
    "hubGroup": "Kupcy i Budowniczowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: stara świnia końcowa.",
        "oldEditionName": "stara świnia końcowa"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Świnia C3.1 nie czeka na punktowanie końcowe pól. Gdy zostanie ukończone miasto z co najmniej jednym herbem, każdy gracz mający świnię na polu stykającym się z tym miastem otrzymuje natychmiastową premię. To zmienia moment decyzji o polu: czasem lepiej punktować wcześniej, zamiast zostawić chłopa do końca."
      },
      {
        "type": "numberedList",
        "items": [
          "Ukończ miasto zawierające co najmniej jeden herb.",
          "Sprawdź pola stykające się z tym miastem.",
          "Każdy gracz ze świnią na takim polu otrzymuje 3 punkty za każde ukończone miasto stykające się z tym polem.",
          "Świnia wraca do zasobu; możesz opcjonalnie zabrać także własnego chłopa z tego pola."
        ]
      },
      {
        "type": "warning",
        "text": "Świnia nie punktuje na końcu gry. Jeżeli nie uruchomi się wcześniej, nie daje końcowej premii."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma świnię na polu styku z dwoma ukończonymi miastami. Trzecie miasto z herbem właśnie zostało zamknięte i styka się z tym samym polem.",
            "Rozpatruje efekt świni natychmiast po ukończeniu miasta z herbem.",
            "1. Miasto ma herb. 2. Pole Celiny styka się z tym miastem. 3. Liczy trzy ukończone miasta przy polu. 4. Celina dostaje 9 punktów. 5. Świnia wraca.",
            "Celina otrzymuje 9 punktów i odzyskuje świnię.",
            "Premia wynosi 3 × liczbę ukończonych miast przy polu świni i działa w trakcie gry."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie świni dopiero podczas punktowania końcowego pól. Korekta: w C3.1 świnia punktuje po ukończeniu miasta z herbem i wraca."
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Podstawowe punktowanie pól"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Efekt uruchamia ukończone miasto z herbem.",
          "Premia: 3 × ukończone miasta przy polu świni.",
          "Świnia wraca po punktowaniu.",
          "Nie ma końcowego punktowania świni."
        ]
      },
      {
        "type": "qa",
        "question": "Czy świnia daje punkty, jeśli miasto bez herbu zostanie ukończone?",
        "answer": "Nie. W C3.1 efekt świni uruchamia wyłącznie ukończenie miasta z co najmniej jednym herbem."
      }
    ]
  },
  {
    "id": "m03-overview",
    "title": "Wróżka i Smok: zagrożenie, ochrona i portal",
    "requiredModuleIds": [
      "major-03-dragon-fairy"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-03",
      "corpus-base"
    ],
    "keywords": [
      "smok",
      "wrozka"
    ],
    "level": "intermediate",
    "hubGroup": "Wróżka i Smok",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Księżniczka i Smok.",
        "oldEditionName": "Księżniczka i Smok"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Wróżka i Smok zmieniają bezpieczeństwo figurek na mapie. Smok usuwa meeple dwoma długimi ruchami przed dołożeniem kafelka, wróżka chroni wybraną figurę i daje jej premię, a portal pozwala wystawić figurę poza nowym kafelkiem. Dopasowanie krawędzi i ukończenie obszarów pozostają zasadami bazy."
      },
      {
        "type": "image",
        "imageId": "dragon-two-moves",
        "alt": "Dwa proste ruchy smoka.",
        "caption": "Smok C3.1"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Smok", "Symbol smoka przed dołożeniem kafelka.", "Wykonuje dwa proste ruchy i usuwa napotkane meeple."],
          ["Wróżka", "Po ukończeniu drogi lub miasta bez własnych punktów.", "Chroni meepla przed smokiem i daje +3 przy jego punktowaniu."],
          ["Portal", "Przy wystawieniu figury z kafelka z portalem.", "Pozwala postawić figurę na innym legalnym, wolnym, nieukończonym obszarze."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Łączenie tej wersji z sześcioma ruchami smoka z C1/C2. Korekta: w C3.1 smok wykonuje dokładnie dwa długie ruchy po prostej."
      },
      {
        "type": "crossReference",
        "sectionId": "m03-dragon",
        "label": "Szczegółowo: smok"
      },
      {
        "type": "crossReference",
        "sectionId": "m03-fairy",
        "label": "Szczegółowo: wróżka"
      },
      {
        "type": "crossReference",
        "sectionId": "m03-portal",
        "label": "Szczegółowo: portal"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Smok działa przed dołożeniem kafelka.",
          "Wróżka chroni i premiuje jedną figurę.",
          "Portal zmienia tylko miejsce wystawienia figury.",
          "Nie używaj zasad Księżniczki i Smoka z wcześniejszych edycji."
        ]
      },
      {
        "type": "qa",
        "question": "Czy smok zmienia legalność kafelków?",
        "answer": "Nie. Po ruchu smoka kafelek nadal musi pasować do wszystkich stykających się krawędzi."
      }
    ],
    "imageIds": [
      "dragon-two-moves"
    ]
  },
  {
    "id": "m03-dragon",
    "title": "Smok: dwa długie ruchy przed dołożeniem",
    "requiredModuleIds": [
      "major-03-dragon-fairy"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-03",
      "corpus-base"
    ],
    "keywords": [
      "smok"
    ],
    "level": "intermediate",
    "hubGroup": "Wróżka i Smok",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Księżniczka i Smok.",
        "oldEditionName": "Księżniczka i Smok"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Symbol smoka przerywa zwykły początek tury. Zanim poznasz orientację nowego kafelka, smok wykonuje dokładnie dwa długie ruchy po prostej do końca pasa kafelków. Usuwa napotkane meeple, a dopiero potem dokładasz kafelek i kontynuujesz turę."
      },
      {
        "type": "numberedList",
        "items": [
          "Dobierz kafelek z symbolem smoka, ale jeszcze go nie dokładasz.",
          "Wykonaj pierwszy ruch smoka po prostej do końca pasa kafelków i usuń napotkane meeple.",
          "Wykonaj drugi ruch smoka w ten sam sposób.",
          "Dopiero potem obróć i dołóż dobrany kafelek, a następnie rozpatrz figurę i ukończenia."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna dobrała kafelek ze smokiem. Na prostej przed smokiem stoją dwie figury bez wróżki.",
            "Najpierw rusza smokiem, potem dopiero dokłada kafelek.",
            "1. Wstrzymuje dołożenie. 2. Wykonuje dwa proste ruchy. 3. Usuwa obie napotkane figury. 4. Dokłada kafelek i kończy turę.",
            "Figury zostały usunięte przed zmianą krajobrazu.",
            "Kolejność C3.1 celowo uniemożliwia najpierw bezpieczne ułożenie nowego kafelka."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Sześć ruchów po jednym polu jak w C1/C2. Korekta: w C3.1 smok wykonuje dokładnie dwa długie ruchy po prostej."
      },
      {
        "type": "crossReference",
        "sectionId": "m03-fairy",
        "label": "Jak wróżka chroni przed smokiem"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Smok działa przed dołożeniem kafelka.",
          "Wykonuje dokładnie dwa ruchy po prostej.",
          "Usuwa napotkane meeple bez wróżki.",
          "Dopiero potem dokładasz kafelek."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę najpierw dołożyć kafelek ze smokiem, a potem ruszyć smokiem?",
        "answer": "Nie. Symbol smoka rozpatruje się przed dołożeniem i orientacją kafelka."
      }
    ]
  },
  {
    "id": "m03-fairy",
    "title": "Wróżka: ochrona i +3 przy punktowaniu",
    "requiredModuleIds": [
      "major-03-dragon-fairy"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-03",
      "corpus-base"
    ],
    "keywords": [
      "wrozka"
    ],
    "level": "intermediate",
    "hubGroup": "Wróżka i Smok",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Księżniczka i Smok.",
        "oldEditionName": "Księżniczka i Smok"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Wróżka jest figurą ochronną i premiującą. Gdy ukończysz drogę lub miasto, ale sam nie zdobywasz przy tym punktów, możesz przypisać wróżkę do własnego meepla. Ten meeple jest chroniony przed smokiem, a przy swoim punktowaniu otrzymuje +3."
      },
      {
        "type": "numberedList",
        "items": [
          "Ukończ drogę albo miasto.",
          "Sprawdź, czy w tym punktowaniu nie otrzymałeś punktów.",
          "Jeśli nie, możesz przypisać wróżkę do jednego własnego meepla.",
          "Przy późniejszym punktowaniu tej figury dodaj +3 i pamiętaj, że smok jej nie usuwa."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek zamyka miasto Anny i nie ma w nim własnego rycerza. Ma jednak własnego podróżnika na innej drodze.",
            "Przypisuje wróżkę do swojego podróżnika.",
            "1. Punktuje miasto dla Anny. 2. Bartek nie dostał punktów. 3. Przypisuje wróżkę do podróżnika. 4. Później droga Bartka daje normalne punkty +3.",
            "Podróżnik Bartka jest chroniony i wart dodatkowe 3 punkty przy swoim punktowaniu.",
            "Warunkiem jest ukończenie drogi lub miasta bez własnych punktów."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Przypisanie wróżki, gdy gracz właśnie zdobył punkty za ukończenie. Korekta: wróżkę możesz przypisać tylko wtedy, gdy sam nie otrzymałeś punktów z tego ukończenia."
      },
      {
        "type": "crossReference",
        "sectionId": "m03-dragon",
        "label": "Kiedy ochrona przed smokiem ma znaczenie"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Wróżkę przypisujesz po ukończeniu drogi lub miasta bez własnych punktów.",
          "Chroni meepla przed smokiem.",
          "Dodaje +3 przy punktowaniu chronionej figury."
        ]
      },
      {
        "type": "qa",
        "question": "Czy wróżka chroni wszystkich meepli gracza?",
        "answer": "Nie. Chroni tylko figurę, do której została przypisana."
      }
    ]
  },
  {
    "id": "m03-portal",
    "title": "Portal: wystawienie figury poza nowym kafelkiem",
    "requiredModuleIds": [
      "major-03-dragon-fairy"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-03",
      "corpus-base"
    ],
    "keywords": [
      "portal"
    ],
    "level": "intermediate",
    "hubGroup": "Wróżka i Smok",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Księżniczka i Smok.",
        "oldEditionName": "Księżniczka i Smok"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Portal nie zmienia reguł zajętości obszaru. Pozwala jedynie, by figura stanęła albo na nowym kafelku, albo na innym legalnym, nieukończonym i niezajętym obszarze krajobrazu. Nie wolno użyć portalu do wejścia na zajęty obszar ani na skarb smoka."
      },
      {
        "type": "numberedList",
        "items": [
          "Dołóż kafelek z portalem legalnie.",
          "Zdecyduj, czy wystawiasz figurę na nowym kafelku, czy przez portal na innym obszarze.",
          "Wybrany obszar musi być legalny, nieukończony i niezajęty.",
          "Nie wystawiaj figury na skarbie smoka."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina dokłada kafelek z portalem. Na tym kafelku nie ma wolnego miasta, ale w krajobrazie jest wolne, nieukończone miasto.",
            "Wystawia rycerza przez portal na tym odległym mieście.",
            "1. Sprawdza, że miasto nie jest zajęte. 2. Sprawdza, że nie jest ukończone. 3. Wystawia rycerza przez portal.",
            "Figura Celiny legalnie zajmuje odległe miasto.",
            "Portal zmienia tylko miejsce wystawienia, nie warunek wolnego i nieukończonego obszaru."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Użycie portalu do wejścia na zajęty obszar. Korekta: portal nie omija zasady zajętości."
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Podstawowe warunki wystawienia figury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Portal pozwala wystawić figurę poza nowym kafelkiem.",
          "Obszar musi być legalny, wolny i nieukończony.",
          "Nie wolno wystawiać figury na skarbie smoka."
        ]
      },
      {
        "type": "qa",
        "question": "Czy portal pozwala wejść do zajętego miasta?",
        "answer": "Nie. Portal nie znosi zakazu wystawiania figury na już zajętym obszarze."
      }
    ]
  },
  {
    "id": "m04-overview",
    "title": "Wieże i Złodzieje: kontrola zasięgu bez porywania",
    "requiredModuleIds": [
      "major-04-towers-thieves"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-04",
      "corpus-base"
    ],
    "keywords": [
      "wieza",
      "straznik"
    ],
    "level": "intermediate",
    "hubGroup": "Wieże i Złodzieje",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wieża (porywanie).",
        "oldEditionName": "Wieża (porywanie)"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Wieże i Złodzieje budują napięcie wokół kontroli odległych pól, ale w C3.1 nie porywają meepli z mapy. Wieże dają zasięg dla strażników, a złodziej okresowo rozlicza tę kontrolę. Podstawowa kolejność tury pozostaje, z dodatkowym efektem przed dołożeniem przy symbolu złodzieja."
      },
      {
        "type": "image",
        "imageId": "tower-range",
        "alt": "Zasięg wieży.",
        "caption": "Zasięg"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Wieża", "Po dołożeniu kafelka możesz budować fundament lub piętro.", "Zasięg równy liczbie pięter, maksymalnie 5."],
          ["Strażnik", "Zamiast zwykłego meepla na nowym kafelku w zasięgu.", "Kontroluje wieżę; zmiana właściciela daje poprzedniemu +2."],
          ["Złodziej", "Przed dołożeniem kafelka ze złodziejem.", "Rozlicza puste wieże i strażników."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Usuwanie figur przeciwnika z mapy przez wieżę. Korekta: ta edycja nie używa porywania meepli."
      },
      {
        "type": "crossReference",
        "sectionId": "m04-towers",
        "label": "Szczegółowo: wieże i strażnicy"
      },
      {
        "type": "crossReference",
        "sectionId": "m04-thieves",
        "label": "Szczegółowo: złodziej"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Wieże kontrolują zasięg, nie porywają meepli.",
          "Strażnik zastępuje zwykłe wystawienie figury.",
          "Złodziej rozlicza wieże przed dołożeniem kafelka.",
          "Na końcu pozostały strażnik jest wart 5 punktów."
        ]
      },
      {
        "type": "qa",
        "question": "Czy wieża może zabrać meepla przeciwnika z drogi lub miasta?",
        "answer": "Nie. W C3.1 Wieże i Złodzieje nie porywają figurek z mapy."
      }
    ],
    "imageIds": [
      "tower-range"
    ]
  },
  {
    "id": "m04-towers",
    "title": "Wieże i strażnicy: budowa, zasięg i zmiana kontroli",
    "requiredModuleIds": [
      "major-04-towers-thieves"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-04",
      "corpus-base"
    ],
    "keywords": [
      "wieza"
    ],
    "level": "intermediate",
    "hubGroup": "Wieże i Złodzieje",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wieża (porywanie).",
        "oldEditionName": "Wieża (porywanie)"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Wieża rośnie przez fundament i kolejne piętra, aż do wysokości 5. Zasięg wieży równa się liczbie jej pięter. Zamiast wystawić zwykłego meepla możesz postawić strażnika na nowym kafelku w zasięgu wieży. Jeśli strażnik zmieni właściciela, poprzedni właściciel dostaje 2 punkty."
      },
      {
        "type": "numberedList",
        "items": [
          "Po dołożeniu kafelka możesz zbudować fundament albo dodać piętro na istniejącej wieży.",
          "Żadna wieża nie przekracza wysokości 5.",
          "Zamiast zwykłego meepla możesz wystawić strażnika na nowym kafelku w zasięgu wieży.",
          "Przy zmianie strażnika poprzedni właściciel otrzymuje 2 punkty."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma wieżę o trzech piętrach. Bartek dokłada kafelek w zasięgu 3 i nie chce wystawiać zwykłego meepla.",
            "Wystawia strażnika w zasięgu wieży.",
            "1. Dokłada kafelek. 2. Sprawdza zasięg 3. 3. Stawia strażnika zamiast meepla.",
            "Wieża Anny jest teraz kontrolowana przez strażnika Bartka.",
            "Strażnik zastępuje zwykłe wystawienie i działa tylko w zasięgu równym liczbie pięter."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Budowanie wieży powyżej 5 pięter. Korekta: maksymalna wysokość wynosi 5."
      },
      {
        "type": "crossReference",
        "sectionId": "m04-thieves",
        "label": "Jak złodziej rozlicza strażników"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Fundament i piętra, maksymalnie wysokość 5.",
          "Zasięg = liczba pięter.",
          "Strażnik zamiast meepla na nowym kafelku w zasięgu.",
          "Zmiana strażnika daje poprzedniemu właścicielowi +2."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę wystawić strażnika i zwykłego meepla w tej samej turze?",
        "answer": "Nie. Strażnik zastępuje zwykłe wystawienie figury w tej turze."
      }
    ]
  },
  {
    "id": "m04-thieves",
    "title": "Złodziej: rozliczenie wież przed dołożeniem",
    "requiredModuleIds": [
      "major-04-towers-thieves"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-04",
      "corpus-base"
    ],
    "keywords": [
      "zlodziej"
    ],
    "level": "intermediate",
    "hubGroup": "Wieże i Złodzieje",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wieża (porywanie).",
        "oldEditionName": "Wieża (porywanie)"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Symbol złodzieja wymusza rozliczenie wież, zanim nowy kafelek wejdzie na planszę. Najpierw dobudujesz piętro na pustych wieżach, potem punktujesz i zwracasz strażników, a dopiero potem dokładasz kafelek. Jeśli nie ma strażników, złodziej zastępuje skradziony kafelek kafelkiem wieży."
      },
      {
        "type": "resolutionOrder",
        "title": "Przed dołożeniem kafelka ze złodziejem",
        "steps": [
          "Dobuduj piętro na pustych wieżach.",
          "Rozstrzygnij i zapunktuj strażników.",
          "Zwróć strażników właścicielom.",
          "Dopiero potem dołóż kafelek złodzieja i kontynuuj turę."
        ]
      },
      {
        "type": "paragraph",
        "text": "Jeżeli nie ma strażników, złodziej zastępuje skradziony kafelek kafelkiem wieży."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina dobrała kafelek ze złodziejem. Na mapie stoi strażnik Anny.",
            "Najpierw rozlicza strażnika, potem dokłada kafelek.",
            "1. Dobudowuje piętra na pustych wieżach. 2. Punktuje strażnika Anny. 3. Zwraca go Annie. 4. Dokłada kafelek złodzieja.",
            "Anna odzyskuje strażnika po punktowaniu, a krajobraz zmienia się dopiero potem.",
            "Złodziej działa przed dołożeniem, aby najpierw rozliczyć kontrolę wież."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Dołożenie kafelka złodzieja przed rozliczeniem strażników. Korekta: najpierw piętra, punktowanie i zwrot strażników, potem dołożenie."
      },
      {
        "type": "crossReference",
        "sectionId": "m04-end",
        "label": "Wartość strażników na końcu gry"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Złodziej działa przed dołożeniem kafelka.",
          "Najpierw piętra na pustych wieżach.",
          "Potem punktowanie i zwrot strażników.",
          "Bez strażników: skradziony kafelek zastępuje kafelek wieży."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę najpierw dołożyć złodzieja, a dopiero potem punktować strażników?",
        "answer": "Nie. Symbol złodzieja rozpatruje się przed dołożeniem kafelka."
      }
    ]
  },
  {
    "id": "m04-end",
    "title": "Wieże i Złodzieje na końcu gry",
    "requiredModuleIds": [
      "major-04-towers-thieves"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-04",
      "corpus-base"
    ],
    "keywords": [
      "straznik"
    ],
    "level": "intermediate",
    "hubGroup": "Wieże i Złodzieje",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wieża (porywanie).",
        "oldEditionName": "Wieża (porywanie)"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Strażnik, który pozostał do końca partii, nadal ma wartość. Podczas punktowania końcowego każdy taki strażnik daje 5 punktów oraz zachowuje skradziony kafelek. Nie stosuj tu reguł porywania i okupu ze starszej Wieży."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Po ostatniej turze Bartek ma jednego strażnika i skradziony kafelek.",
            "Liczy strażnika podczas punktowania końcowego.",
            "1. Potwierdza, że strażnik pozostał. 2. Przyznaje 5 punktów. 3. Zachowuje skradziony kafelek według zasady dodatku.",
            "Bartek otrzymuje 5 punktów za strażnika.",
            "Końcowa wartość strażnika wynosi 5 i obejmuje zachowanie skradzionego kafelka."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pomijanie końcowych 5 punktów za pozostałego strażnika. Korekta: strażnik na końcu gry jest wart 5 punktów."
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Ogólna kolejność punktowania końcowego"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Pozostały strażnik: 5 punktów.",
          "Zachowuje skradziony kafelek.",
          "Nie stosuj zasad porywania ze starszej Wieży."
        ]
      },
      {
        "type": "qa",
        "question": "Czy strażnik bez skradzionego kafelka też daje 5 punktów?",
        "answer": "Tak. Końcowa wartość 5 dotyczy pozostałego strażnika; skradziony kafelek jest dodatkowym elementem zasady, gdy został wcześniej zdobyty."
      }
    ]
  },
  {
    "id": "m05-overview",
    "title": "Kurierzy i Burmistrzowie: alternatywy w zwykłej turze",
    "requiredModuleIds": [
      "major-05-messengers-mayors"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-05",
      "corpus-base"
    ],
    "keywords": [
      "ratusz",
      "burmistrz"
    ],
    "level": "intermediate",
    "hubGroup": "Kurierzy i Burmistrzowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Opactwo i Burmistrz.",
        "oldEditionName": "Opactwo i Burmistrz"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Kurierzy i Burmistrzowie dają narzędzia do zmiany rytmu tury, a nie nową fazę gry. Ratusz zastępuje dobrany kafelek, burmistrz i kurier pełnią specjalne role na mieście i drodze, a strach na wróble blokuje chłopów. Nie mieszaj tego dodatku z minidodatkiem Wiadomości ani ze starym Opactwem i Burmistrzem."
      },
      {
        "type": "image",
        "imageId": "town-hall-mayor",
        "alt": "Ratusz i figury.",
        "caption": "Exp 5"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Ratusz", "Zamiast dobranego kafelka.", "Zawsze pasuje, domyka obszar, bez wystawienia meepla."],
          ["Burmistrz", "W mieście zamiast zwykłej figury.", "Siła 0; przy czterech sąsiadach uruchamia premię."],
          ["Kurier", "Na drodze.", "Siła 1; po punktowaniu drogi może się przenieść."],
          ["Strach na wróble", "Na polu.", "Blokuje wystawienie chłopa na tym polu."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Mylenie Kurierów z minidodatkiem Wiadomości. Korekta: to osobne moduły o innych zasadach."
      },
      {
        "type": "crossReference",
        "sectionId": "m05-town-hall",
        "label": "Szczegółowo: ratusz"
      },
      {
        "type": "crossReference",
        "sectionId": "m05-figures",
        "label": "Szczegółowo: burmistrz, kurier i strach"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Dodatek zmienia wybory w zwykłej turze.",
          "Ratusz może domknąć obszar bez meepla.",
          "Burmistrz ma siłę 0, kurier siłę 1.",
          "Po ostatnich ratuszach kończy się partia."
        ]
      },
      {
        "type": "qa",
        "question": "Czy ten dodatek tworzy dodatkową fazę tury?",
        "answer": "Nie. Jego elementy są alternatywami dla zwykłego doboru kafelka albo wystawienia figury."
      }
    ],
    "imageIds": [
      "town-hall-mayor"
    ]
  },
  {
    "id": "m05-town-hall",
    "title": "Ratusz: zawsze pasuje, domyka, kończy grę",
    "requiredModuleIds": [
      "major-05-messengers-mayors"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-05",
      "corpus-base"
    ],
    "keywords": [
      "ratusz"
    ],
    "level": "intermediate",
    "hubGroup": "Kurierzy i Burmistrzowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Opactwo i Burmistrz.",
        "oldEditionName": "Opactwo i Burmistrz"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Ratusz kładziesz zamiast dobranego kafelka krajobrazu. Pasuje zawsze i może domknąć obszar, lecz nie pozwala wystawić meepla. Po wykorzystaniu ostatniego ratusza kończy się partia: rozstrzygnij bieżącą turę, a następnie standardowe punktowanie końcowe."
      },
      {
        "type": "numberedList",
        "items": [
          "Zamiast dobranego kafelka wybierz ratusz.",
          "Połóż go; pasuje zawsze i może domknąć obszar.",
          "Nie wystawiaj meepla w tej turze.",
          "Rozpatrz ukończenia wywołane ratuszem.",
          "Jeśli był to ostatni ratusz, zakończ partię po tej turze i przejdź do finału."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma niekorzystny kafelek, a obok leży prawie zamknięte miasto Bartka.",
            "Kładzie ratusz zamiast kafelka i zamyka miasto.",
            "1. Odklada dobrany kafelek. 2. Kładzie ratusz. 3. Nie wystawia figury. 4. Punktuje ukończone miasto według większości.",
            "Miasto zostaje zamknięte bez nowej figury Anny.",
            "Ratusz domyka obszar, ale nie daje wystawienia meepla."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Wystawienie meepla po położeniu ratusza. Korekta: ratusz nie pozwala wystawić figury."
      },
      {
        "type": "crossReference",
        "sectionId": "game-end",
        "label": "Jak kończy się partia po ostatnim ratuszu"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ratusz kładziesz zamiast dobranego kafelka.",
          "Zawsze pasuje i może domknąć obszar.",
          "Nie pozwala wystawić meepla.",
          "Ostatni ratusz kończy partię po bieżącej turze."
        ]
      },
      {
        "type": "qa",
        "question": "Czy ratusz musi pasować do krawędzi jak zwykły kafelek?",
        "answer": "Nie. Ratusz zawsze pasuje, dlatego może domknąć obszar nawet wtedy, gdy zwykły kafelek by nie pasował."
      }
    ]
  },
  {
    "id": "m05-figures",
    "title": "Burmistrz, kurier i strach na wróble",
    "requiredModuleIds": [
      "major-05-messengers-mayors"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-05",
      "corpus-base"
    ],
    "keywords": [
      "burmistrz",
      "kurier",
      "strach"
    ],
    "level": "intermediate",
    "hubGroup": "Kurierzy i Burmistrzowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Opactwo i Burmistrz.",
        "oldEditionName": "Opactwo i Burmistrz"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Trzy specjalne figury zmieniają decyzję o zajęciu obszaru. Burmistrz wchodzi do miasta z siłą 0 i może uruchomić premię przy czterech sąsiadach. Kurier zajmuje drogę z siłą 1 i po jej punktowaniu może się przenieść. Strach na wróble zajmuje pole tak, że nie można już postawić tam chłopa."
      },
      {
        "type": "numberedList",
        "items": [
          "Burmistrza wystawiasz w mieście według zwykłych zasad zajętości; ma siłę 0.",
          "Gdy burmistrz ma czterech sąsiadów, uruchamia wskazaną premię dodatku.",
          "Kuriera wystawiasz na drodze; po punktowaniu tej drogi może się przenieść według własnej reguły.",
          "Strach na wróble kładziesz na polu; na tym polu nie wolno już wystawić chłopa."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek ma burmistrza w mieście z czterema sąsiadami i meeplem przeciwnika.",
            "Uruchamia premię burmistrza.",
            "1. Sprawdza czterech sąsiadów. 2. Rozpatruje premię wskazaną przez dodatek. 3. Kontynuuje punktowanie miasta według większości, pamiętając o sile 0 burmistrza.",
            "Premia burmistrza działa, ale nie daje mu siły 1 w większości.",
            "Burmistrz ma siłę 0; jego wartość leży w premii przy czterech sąsiadach."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Traktowanie burmistrza jako zwykłej siły 1. Korekta: burmistrz ma siłę 0."
      },
      {
        "type": "commonMistake",
        "text": "Wystawienie chłopa na pole ze strachem na wróble. Korekta: strach blokuje chłopów na tym polu."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Jak siła 0 burmistrza wpływa na większość"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Burmistrz: miasto, siła 0, premia przy 4 sąsiadach.",
          "Kurier: droga, siła 1, może się przenieść po punktowaniu.",
          "Strach na wróble: pole, blokuje chłopów."
        ]
      },
      {
        "type": "qa",
        "question": "Czy burmistrz pomaga wygrać większością w mieście?",
        "answer": "Nie przez siłę. Jego siła wynosi 0; wartość daje premia przy czterech sąsiadach."
      }
    ]
  },
  {
    "id": "m05-examples",
    "title": "Kurierzy i Burmistrzowie: przykłady i typowe błędy",
    "requiredModuleIds": [
      "major-05-messengers-mayors"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-05",
      "corpus-base"
    ],
    "keywords": [
      "przyklad"
    ],
    "level": "intermediate",
    "hubGroup": "Kurierzy i Burmistrzowie",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Opactwo i Burmistrz.",
        "oldEditionName": "Opactwo i Burmistrz"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Te przykłady łączą ratusz i specjalne figury w sytuacjach, które najczęściej mylą początkujących. Pokazują, że ratusz zamyka bez figury, burmistrz działa przez premię, a minidodatek Wiadomości nie zastępuje Kurierów tego rozszerzenia."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma burmistrza w mieście z czterema sąsiadami i figurą przeciwnika.",
            "Uruchamia premię favor zamiast liczyć burmistrza jako siłę 1.",
            "1. Potwierdza czterech sąsiadów. 2. Rozpatruje premię zamiany lub inny wskazany efekt. 3. Punktuje miasto bez dodawania siły 1 za burmistrza.",
            "Premia działa, a większość liczy tylko rzeczywiste siły.",
            "Burmistrz nie jest zwykłym meeplem o sile 1."
          ]
        ]
      },
      {
        "type": "example",
        "title": "Ratusz bez meepla",
        "text": "Ratusz może domknąć miasto bez pasowania krawędzi, lecz nie pozwala dodatkowo postawić meepla — najpierw rozstrzygasz zamknięcie."
      },
      {
        "type": "commonMistake",
        "text": "Mylenie z minidodatkiem Kurierzy/Wiadomości. Korekta: to osobny moduł; nie przenoś jego kart ani zasad do Kurierów i Burmistrzów."
      },
      {
        "type": "crossReference",
        "sectionId": "m05-town-hall",
        "label": "Pełna procedura ratusza"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ratusz zamyka bez wystawienia figury.",
          "Burmistrz działa przez premię, nie przez siłę 1.",
          "Nie mieszaj z minidodatkiem Wiadomości."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę używać kart minidodatku Wiadomości razem z Kurierami z tego rozszerzenia?",
        "answer": "Nie jako jednej połączonej zasady. To osobne moduły; stosuj tylko aktywny moduł i jego własny opis."
      }
    ]
  }
];
